import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactClick = () => {
    if (location.pathname === '/') {
      // Already on home page, just scroll to contact
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page and then scroll to contact
      navigate('/', { replace: true });
      setTimeout(() => {
        const contactElement = document.getElementById('contact');
        if (contactElement) {
          contactElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const navItems = [
    { label: 'الرئيسية', href: '/' },
    { label: 'خدماتنا', href: '/services' },
    { label: 'القيادة', href: '/leadership' }
  ];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-government' 
          : 'bg-transparent'
        }
      `}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center"
            >
              <img 
                src="/logo_1to1.png" 
                alt="ضوء الخريج" 
                className="h-8 md:h-10 w-auto object-contain rounded-xl"
                style={{ aspectRatio: 'auto' }}
              />
              <span className="mr-3 text-xl md:text-2xl font-bold text-government-navy font-heading">
                ضوء الخريج
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 space-x-reverse font-heading">
            {navItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                <Link
                  to={item.href}
                  className="text-government-navy hover:text-brand-primary font-medium transition-colors duration-200 relative group font-heading"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-primary transition-all duration-200 group-hover:w-full font-heading"></span>
                </Link>
              </motion.div>
            ))}
            {/* Contact Link - Special handling */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navItems.length * 0.1 + 0.3 }}
            >
              <button
                onClick={handleContactClick}
                className="text-government-navy hover:text-brand-primary font-medium transition-colors duration-200 relative group font-heading"
              >
                تواصل معنا
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-primary transition-all duration-200 group-hover:w-full"></span>
              </button>
            </motion.div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a 
              href="https://calendly.com/fahad-alkhreej/meet-with-me"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="primary" size="md" className='font-heading'>
                حدد موعد شراكة
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-government-navy hover:text-brand-primary transition-colors duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ 
          opacity: isMobileMenuOpen ? 1 : 0, 
          height: isMobileMenuOpen ? 'auto' : 0 
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden bg-white border-t border-gray-200 overflow-hidden"
      >
        <div className="section-container py-4">
          <nav className="flex flex-col space-y-4">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="text-government-navy hover:text-brand-primary font-medium py-2 transition-colors duration-200 font-heading"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            {/* Contact Link - Special handling */}
            <button
              onClick={() => {
                handleContactClick();
                setIsMobileMenuOpen(false);
              }}
              className="text-government-navy hover:text-brand-primary font-medium py-2 transition-colors duration-200 text-right font-heading"
            >
              تواصل معنا
            </button>
            <div className="pt-4 border-t border-gray-200">
              <a 
                href="https://calendly.com/fahad-alkhreej/meet-with-me"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="primary" size="md" className="w-full font-heading">
                  حدد موعد شراكة
                </Button>
              </a>
            </div>
          </nav>
        </div>
      </motion.div>
    </motion.header>
  );
};